import {useMemo} from "react";
import _ from "lodash";
import {Stack} from "@mui/material";
import {VirtualSoilMoistureProbeChart} from "./charts/VirtualSoilMoistureProbeChart";
import {ActualEvapotranspirationChart} from "./charts/ActualEvapotranspirationChart";
import {VirtualTensiometerChart} from "./charts/VirtualTensiometerChart";
import {EvapotranspirationAppliedWaterCumulativeChart} from "./charts/EvapotranspirationAppliedWaterCumulativeChart";
import {FieldIrrigationPlannerCard} from "./FieldIrrigationPlannerCard";
import {FieldAdvisedIrrigationCard} from "./FieldAdvisedIrrigationCard";
import {IrrigationPlannerDataProps} from "../../types/irrigationPlannerDataProps";
import {FieldResult} from "../../types/domain/fieldResult";
import {getIrrigationPlannerData} from "../../utils/irrigationPlannerUtils";
import {authenticatedUserLoadable} from "../../store/authenticatedUserStore";
import {useLoadableValueOrDefault} from "../../hooks/jotaiHooks";
import {User} from "../../types/domain/user";
import {EtawCumulativeChart} from "./charts/EtawCumulativeChart";
import {isMaderaCompany} from "../../utils/maderaUtils";
import {useAccessMarker} from "../../hooks/accessMarkerHooks";
import {ALL_ACCESS_FUNCTIONS} from "../../utils/accessControlUtils";
import {useMeasureSystemUtils} from "../../hooks/measureSystemUtilsHooks";
import {useCompany} from "../../hooks/companiesHooks";

export interface Props {
    data: FieldResult[];
}

export const FieldIrrigationInsightsView: React.FC<Props> = ({data}) => {
    const company = useCompany();
    const isMadera = company?.data && isMaderaCompany(company?.data);
    const authenticatedUser = useLoadableValueOrDefault<User | undefined>(authenticatedUserLoadable, undefined);
    const isAdvanced = authenticatedUser?.preferences?.enabledAdvancedFeatures;
    const access = useAccessMarker(ACCESS_FUNCTIONS);
    const msUtils = useMeasureSystemUtils();

    const irrigationPlannerData = useMemo<IrrigationPlannerDataProps[]>(() => {
        if (data.length > 0) {
            const recentDataItem = data[data.length - 1];
            const irrigationPlannerData = getIrrigationPlannerData(recentDataItem);

            return irrigationPlannerData.map<IrrigationPlannerDataProps>((item) => ({
                date: item.date,
                irrigationForecast: msUtils.getRainfall(item.irrigationForecast),
                precipitationForecast: msUtils.getRainfall(item.precipitationForecast),
            }));
        } else {
            return [];
        }
    }, [data, msUtils]);

    return (
        <Stack direction="column" gap="1.25rem">
            <Stack direction="row" gap="1.25rem">
                <FieldAdvisedIrrigationCard data={irrigationPlannerData} />
                <FieldIrrigationPlannerCard data={irrigationPlannerData} />
            </Stack>
            <VirtualSoilMoistureProbeChart data={data} />
            {isMadera && <EtawCumulativeChart data={data} />}
            {isAdvanced && <ActualEvapotranspirationChart data={data} />}
            {isAdvanced && <VirtualTensiometerChart data={data} />}
            {isAdvanced && <EvapotranspirationAppliedWaterCumulativeChart data={data} />}
            {!isMadera && access.canViewEtawCumulativeChart && <EtawCumulativeChart data={data} />}
        </Stack>
    );
};

const ACCESS_FUNCTIONS = _.pick(ALL_ACCESS_FUNCTIONS, ["canViewEtawCumulativeChart"]);
